function buildQuery(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return ''
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, String(value))
    }
  }
  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

export const endpoints = {
  auth: {
    requestOtp: '/admin/auth/request-otp',
    verifyOtp: '/admin/auth/verify-otp',
    logout: '/admin/auth/logout',
    me: '/admin/auth/me',
  },

  accounts: {
    list: (params?: {
      page?: number
      per_page?: number
      search?: string
      status?: string
      tier?: string
    }): string => `/admin/accounts${buildQuery(params)}`,
    detail: (id: string): string => `/admin/accounts/${id}`,
    pause: (id: string): string => `/admin/accounts/${id}/pause`,
    resume: (id: string): string => `/admin/accounts/${id}/resume`,
    tier: (id: string): string => `/admin/accounts/${id}/tier`,
    overrides: (id: string): string => `/admin/accounts/${id}/overrides`,
    credits: (id: string): string => `/admin/accounts/${id}/credits`,
    resetOverage: (id: string): string => `/admin/accounts/${id}/reset-overage`,
    createPilot: (id: string): string => `/admin/accounts/${id}/create-pilot`,
    audit: (id: string): string => `/admin/accounts/${id}/audit`,
  },

  emails: {
    list: (params?: {
      page?: number
      per_page?: number
      status?: string
      search?: string
      account?: string
      domain?: string
      from?: string
      to?: string
    }): string => `/admin/emails${buildQuery(params)}`,
    detail: (id: string): string => `/admin/emails/${id}`,
  },

  domains: {
    list: (params?: {
      page?: number
      per_page?: number
      search?: string
      status?: string
      account?: string
      sort?: string
      direction?: string
    }): string => `/admin/domains${buildQuery(params)}`,
    reverify: (id: string): string => `/admin/domains/${id}/reverify`,
  },

  analytics: {
    overview: (params?: { from?: string; to?: string; granularity?: string }): string =>
      `/admin/analytics${buildQuery(params)}`,
    bounces: (params?: { from?: string; to?: string }): string =>
      `/admin/analytics/bounces${buildQuery(params)}`,
    isps: (params?: { from?: string; to?: string }): string =>
      `/admin/analytics/isps${buildQuery(params)}`,
  },

  engine: {
    health: '/admin/engine/health',
    queueStats: '/admin/engine/queue/stats',
    metrics: '/admin/engine/metrics',
    queueFlush: '/admin/engine/queue/flush',
  },

  billing: {
    summary: '/admin/billing/summary',
    overages: '/admin/billing/overages',
    polarEvents: '/admin/billing/polar-events',
    polarSync: '/admin/billing/polar-sync',
  },

  alerts: {
    rules: '/admin/alerts/rules',
    updateRule: (id: string): string => `/admin/alerts/rules/${id}`,
    history: (params?: { page?: number; per_page?: number }): string =>
      `/admin/alerts/history${buildQuery(params)}`,
    test: '/admin/alerts/test',
  },

  adminUsers: {
    list: '/admin/users',
    create: '/admin/users',
    remove: (id: string): string => `/admin/users/${id}`,
    changeRole: (id: string): string => `/admin/users/${id}/role`,
  },

  audit: {
    list: (params?: {
      page?: number
      per_page?: number
      admin_user?: string
      action?: string
      target_type?: string
      target_id?: string
      from?: string
      to?: string
    }): string => `/admin/audit${buildQuery(params)}`,
  },

  seeds: {
    list: '/admin/seeds',
    delete: (id: string): string => `/admin/seeds/${id}`,
    test: '/admin/seeds/test',
    results: (params?: { page?: number; per_page?: number }): string =>
      `/admin/seeds/results${buildQuery(params)}`,
  },

  abuse: {
    overview: (): string => '/admin/abuse/overview',
    enforcementLog: (params?: {
      page?: number
      per_page?: number
      account_id?: string
      action?: string
    }): string => `/admin/abuse/enforcement-log${buildQuery(params)}`,
    atRiskAccounts: (): string => '/admin/abuse/at-risk-accounts',
    globalSuppressions: (params?: { page?: number; per_page?: number; search?: string }): string =>
      `/admin/abuse/global-suppressions${buildQuery(params)}`,
    contentBlocks: (): string => '/admin/abuse/content-blocks',
  },

  builderApplications: {
    list: (params?: { page?: number; per_page?: number; status?: string; type?: string }): string =>
      `/admin/builder-applications${buildQuery(params)}`,
    update: (id: string): string => `/admin/builder-applications/${id}`,
  },

  ips: {
    list: '/admin/ips',
    detail: (id: string): string => `/admin/ips/${id}`,
    pause: (id: string): string => `/admin/ips/${id}/pause`,
    resume: (id: string): string => `/admin/ips/${id}/resume`,
    reset: (id: string): string => `/admin/ips/${id}/reset`,
    advance: (id: string): string => `/admin/ips/${id}/advance`,
  },

  boxes: {
    list: '/admin/boxes',
    detail: (id: string): string => `/admin/boxes/${id}`,
    poolStatus: '/admin/boxes/pool-status',
    update: (id: string): string => `/admin/boxes/${id}`,
    weight: (id: string): string => `/admin/boxes/${id}/weight`,
    pause: (id: string): string => `/admin/boxes/${id}/pause`,
    resume: (id: string): string => `/admin/boxes/${id}/resume`,
  },

  bugReports: {
    list: (params?: {
      page?: number
      per_page?: number
      status?: string
      category?: string
    }): string => `/admin/bug-reports${buildQuery(params)}`,
    update: (id: string): string => `/admin/bug-reports/${id}`,
  },

  status: {
    overview: '/admin/status',
    uptime: (serviceId: string): string => `/admin/status/uptime/${serviceId}`,
    incidentsActive: '/admin/status/incidents/active',
    incidentsRecent: '/admin/status/incidents/recent',
    createIncident: '/admin/status/incidents',
    updateIncident: (id: string): string => `/admin/status/incidents/${id}`,
    addUpdate: (id: string): string => `/admin/status/incidents/${id}/updates`,
    deleteIncident: (id: string): string => `/admin/status/incidents/${id}`,
    maintenance: '/admin/status/maintenance',
  },

  settings: {
    get: '/admin/settings',
    testConnection: '/admin/settings/test-connection',
    slack: '/admin/settings/slack',
    testSlack: '/admin/settings/test-slack',
  },

  staging: {
    apiHealth: '/admin/staging/health/api',
    adminHealth: '/admin/staging/health/admin',
    cronHealth: '/admin/staging/health/cron',
    ingestHealth: '/admin/staging/health/ingest',
    unsubHealth: '/admin/staging/health/unsub',
    metrics: '/admin/staging/metrics',
  },

  infra: {
    current: '/admin/infra/current',
    history: (host: string, period: string): string =>
      `/admin/infra/history?host=${encodeURIComponent(host)}&period=${period}`,
    thresholds: '/admin/infra/thresholds',
    updateThreshold: '/admin/infra/thresholds',
  },
} as const
